import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { money } from "../../lib/pricing";
import { openAuthFile } from "../../lib/fileOpen";
import { Button } from "../ui/button";
import { Money, FilePdf } from "@phosphor-icons/react";
import { Section, Tile, Pill } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

/** My HR: payslips, P60s / P45 and the leave balance for the payroll record linked to this login. */
export function PayCard() {
  const [d, setD] = useState(null);
  const [p60s, setP60s] = useState([]);
  const [p45, setP45] = useState(null);
  const [leave, setLeave] = useState(null);
  useEffect(() => {
    API.get("/my/payslips").then((r) => setD(r.data)).catch(() => setD({ employee: null, payslips: [] }));
    API.get("/my/p60s").then((r) => setP60s(r.data.p60s || [])).catch(() => setP60s([]));
    API.get("/my/p45").then((r) => setP45(r.data.p45)).catch(() => setP45(null));
    API.get("/my/leave").then((r) => setLeave(r.data)).catch(() => setLeave(null));
  }, []);
  const latest = d?.payslips?.[0];
  const bal = leave?.balance;
  return (
    <Section title="Pay & leave" icon={Money} testid="my-hr-pay" action={d?.employee && <Link to="/my-work/payslips" className="text-xs text-primary underline" data-testid="my-hr-pay-all">All payslips</Link>}>
      {!d ? <p className="text-xs text-muted-foreground">Loading…</p> : !d.employee ? (
        <p className="text-sm text-muted-foreground" data-testid="my-hr-pay-none">Your record isn't linked to payroll yet — once HR links it your payslips, P60s and leave balance show here.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label="Latest net pay" value={latest ? money(latest.net) : "—"} hint={latest ? `paid ${fmt(latest.pay_date)}` : "no approved pay run yet"} testid="my-hr-pay-net" />
            <Tile label="Tax code" value={d.employee.tax_code || "—"} hint={d.employee.payroll_id} testid="my-hr-pay-taxcode" />
            <Tile label="Leave left" value={bal ? `${bal.available}` : "—"} hint={bal ? `of ${bal.entitlement} days · ${bal.pending} pending` : ""} testid="my-hr-pay-leave" />
            <Tile label="Leave taken" value={bal ? `${bal.taken}` : "—"} hint={bal ? `year ${fmt(bal.year_start)} → ${fmt(bal.year_end)}` : ""} testid="my-hr-pay-taken" />
          </div>
          <div className="flex flex-wrap gap-2">
            {p45 && <Button size="sm" variant="outline" onClick={() => openAuthFile("/my/p45.pdf")} data-testid="my-hr-p45"><FilePdf size={14} className="mr-1" /> P45</Button>}
            {p60s.map((p) => <Button key={p.tax_year} size="sm" variant="outline" onClick={() => openAuthFile(`/my/p60s/${p.tax_year}.pdf`)} data-testid={`my-hr-p60-${p.tax_year}`}><FilePdf size={14} className="mr-1" /> P60 {p.label}</Button>)}
          </div>
          {d.payslips.length > 0 && (
            <ul className="divide-y divide-border text-sm" data-testid="my-hr-payslips">{d.payslips.slice(0, 6).map((p) => (
              <li key={p.run_id} className="py-2 flex items-center justify-between gap-2">
                <div><p className="font-medium">{fmt(p.period_start)} – {fmt(p.period_end)}</p><p className="text-xs text-muted-foreground">Paid {fmt(p.pay_date)} · gross {money(p.gross)} · tax {money(p.tax)} · NI {money(p.ni_employee)}</p></div>
                <div className="flex items-center gap-2"><span className="font-semibold tabular-nums">{money(p.net)}</span><Button size="sm" variant="ghost" className="h-7" onClick={() => openAuthFile(`/my/payslips/${p.run_id}.pdf`)} data-testid={`my-hr-payslip-${p.run_id}`}><FilePdf size={14} /></Button></div>
              </li>
            ))}</ul>
          )}
          {leave?.leave?.length > 0 && (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-1 items-center">Upcoming leave: {leave.leave.filter((l) => l.status !== "cancelled" && l.end >= new Date().toISOString().slice(0, 10)).slice(0, 4).map((l) => <Pill key={l.id} tone={l.status === "approved" ? TONE_CLASSES.ok : TONE_CLASSES.soon}>{fmt(l.start)}{l.end !== l.start ? ` → ${fmt(l.end)}` : ""} · {l.status}</Pill>)}</div>
          )}
        </div>
      )}
    </Section>
  );
}
